import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { History, RefreshCw, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { API_URL } from "../../lib/utils";

const KIND_CLS = {
  cash_close: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  low_stock: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  security: "bg-red-500/15 text-red-700 dark:text-red-300",
  daily_summary: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  saft_reminder: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
};
const FILTERS = [["", "Todos"], ["cash_close", "Fecho de caixa"], ["low_stock", "Stock"], ["security", "Segurança"], ["daily_summary", "Resumo diário"], ["saft_reminder", "SAF-T"]];
const dt = (iso) => new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });

// Histórico dos avisos push enviados à empresa (data, tipo, texto e quem recebeu)
export const PushLogList = () => {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k = kind) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/push/log`, { params: k ? { kind: k, limit: 100 } : { limit: 100 }, withCredentials: true });
      setRows(r.data);
    } catch { toast.error("Erro ao carregar histórico de notificações"); } finally { setLoading(false); }
  };
  useEffect(() => { load(kind); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card data-testid="push-log-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base md:text-lg"><History className="w-5 h-5 text-cyan-500" /> Histórico de notificações</CardTitle>
            <CardDescription>Avisos push já enviados aos gerentes desta empresa — com data, tipo e quem recebeu.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-md border overflow-hidden">
              {FILTERS.map(([id, label]) => (
                <button key={id} type="button" onClick={() => setKind(id)} className={`px-2.5 h-8 text-xs ${kind === id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`push-log-filter-${id || "all"}`}>{label}</button>
              ))}
            </div>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => load()} disabled={loading} data-testid="push-log-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 && <p className="text-sm text-muted-foreground text-center py-6" data-testid="push-log-empty">Ainda não foi enviado nenhum aviso{kind ? " deste tipo" : ""}.</p>}
        <ul className="divide-y" data-testid="push-log-list">
          {rows.map((r) => (
            <li key={r.log_id || r.created_at} className="py-3 flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-4" data-testid={`push-log-row-${r.log_id || r.created_at}`}>
              <div className="shrink-0 sm:w-36">
                <p className="text-xs font-mono text-muted-foreground">{dt(r.created_at)}</p>
                <Badge variant="secondary" className={`mt-1 ${KIND_CLS[r.kind] || ""}`} data-testid="push-log-kind">{r.kind_label || r.kind}</Badge>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{r.title}</p>
                <p className="text-xs text-muted-foreground whitespace-pre-line">{r.body}</p>
                <p className="text-xs mt-1 flex items-start gap-1" data-testid="push-log-recipients">
                  <Users className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  {r.sent > 0
                    ? <span>Recebido por <strong>{(r.recipients || []).map((p) => p.name).join(", ") || `${r.sent} dispositivo(s)`}</strong>{r.devices ? ` · ${r.sent}/${r.devices} dispositivo(s)` : ""}</span>
                    : <span className="text-amber-600">Não entregue a nenhum dispositivo{r.devices ? ` (${r.devices} tentado(s))` : ""}</span>}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
